import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Target, TrendingUp, Users, Calendar, Star, BarChart3, Activity } from 'lucide-react';
import { fetchStatsEquipeChampionnat, fetchEngagementsEquipe } from '../../api/fff';

const StatsN2Section = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      // Charger les engagements pour récupérer les IDs des compétitions
      const engagements = await fetchEngagementsEquipe('2036473'); // ID de l'équipe N2
      
      // Trouver l'engagement de championnat
      const championnat = engagements.find(e => e.competition.type === 'CH');
      
      if (championnat) {
        const statsData = await fetchStatsEquipeChampionnat({
          cpNo: championnat.competition.cp_no,
          phNo: championnat.phase.number,
          gpNo: championnat.poule.stage_number,
          nomEquipe: 'FEIGNIES/AULNOYE EFC'
        });
        
        if (statsData) {
          setStats(statsData);
        }
      }
    } catch (error) {
      // En cas d'erreur, on continue silencieusement
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur lors du chargement des stats:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateForme = () => {
    if (!stats || !stats.forme || !Array.isArray(stats.forme)) return 0;
    const recentForm = stats.forme.slice(-5); // 5 derniers matchs
    const points = recentForm.reduce((acc, result) => {
      if (result === 'V') return acc + 3;
      if (result === 'N') return acc + 1;
      return acc;
    }, 0);
    return Math.round((points / (recentForm.length * 3)) * 100);
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 80) return 'text-green-400';
    if (percentage >= 60) return 'text-yellow-400';
    if (percentage >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-[#001B56] to-[#00238D]">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-64 mx-auto mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={`loading-skeleton-${i}`} className="h-32 bg-gray-700 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!stats) {
    return null;
  }

  const formePercentage = calculateForme();
  const totalMatchs = stats.total_games_count || 0;
  const winRate = totalMatchs > 0 ? Math.round((stats.won_games_count / totalMatchs) * 100) : 0;
  const avgGoalsFor = totalMatchs > 0 ? (stats.goals_for_count / totalMatchs).toFixed(1) : 0;
  const avgGoalsAgainst = totalMatchs > 0 ? (stats.goals_against_count / totalMatchs).toFixed(1) : 0;

  return (
    <section className="py-16 bg-gradient-to-br from-[#001B56] to-[#00238D] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 border-2 border-[#FFE400] rounded-full"></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 border-2 border-[#BDFF00] rounded-full"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-[#FFE400] rounded-full blur-xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-title text-3xl md:text-5xl font-bold bg-gradient-to-r from-[#FFE400] to-[#BDFF00] bg-clip-text text-transparent mb-4">
            STATISTIQUES N2
          </h2>
          <p className="font-body text-gray-300 text-lg max-w-2xl mx-auto">
            Performances et statistiques de notre équipe première en temps réel
          </p>
        </motion.div>

        {/* Onglets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex justify-center mb-8"
        >
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-2">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('general')}
                className={`px-6 py-3 rounded-xl font-accent font-medium transition-all ${
                  activeTab === 'general'
                    ? 'bg-[#FFE400] text-[#001B56] shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                Général
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`px-6 py-3 rounded-xl font-accent font-medium transition-all ${
                  activeTab === 'performance'
                    ? 'bg-[#FFE400] text-[#001B56] shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                Performance
              </button>
              <button
                onClick={() => setActiveTab('attaque')}
                className={`px-6 py-3 rounded-xl font-accent font-medium transition-all ${
                  activeTab === 'attaque'
                    ? 'bg-[#FFE400] text-[#001B56] shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                Attaque
              </button>
            </div>
          </div>
        </motion.div>

        {/* Contenu des onglets */}
        {activeTab === 'general' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {/* Classement */}
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
            >
              <div className="flex items-center justify-between mb-4">
                <Trophy className="text-[#FFE400]" size={28} />
                <span className="text-xs font-accent text-gray-400 uppercase tracking-wider">Classement</span>
              </div>
              <div className="text-3xl font-title font-bold text-white mb-2">
                {stats.rank}<sup className="text-lg">e</sup>
              </div>
              <div className="text-sm font-body text-gray-300">
                National 2 Poule C
              </div>
              <div className="mt-4 text-xs font-accent text-[#BDFF00]">
                {stats.point_count} points
              </div>
            </motion.div>

            {/* Bilan */}
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
            >
              <div className="flex items-center justify-between mb-4">
                <BarChart3 className="text-[#BDFF00]" size={28} />
                <span className="text-xs font-accent text-gray-400 uppercase tracking-wider">Bilan</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-lg font-title font-bold text-green-400">{stats.won_games_count}</div>
                  <div className="text-xs font-body text-gray-400">V</div>
                </div>
                <div>
                  <div className="text-lg font-title font-bold text-yellow-400">{stats.draw_games_count}</div>
                  <div className="text-xs font-body text-gray-400">N</div>
                </div>
                <div>
                  <div className="text-lg font-title font-bold text-red-400">{stats.lost_games_count}</div>
                  <div className="text-xs font-body text-gray-400">D</div>
                </div>
              </div>
              <div className="mt-4 text-xs font-accent text-gray-300">
                {totalMatchs} matchs joués
              </div>
            </motion.div>

            {/* Buts */}
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
            >
              <div className="flex items-center justify-between mb-4">
                <Target className="text-[#FFE400]" size={28} />
                <span className="text-xs font-accent text-gray-400 uppercase tracking-wider">Buts</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-title font-bold text-green-400">{stats.goals_for_count}</div>
                  <div className="text-xs font-body text-gray-400">Marqués</div>
                </div>
                <div className="text-gray-500">:</div>
                <div>
                  <div className="text-2xl font-title font-bold text-red-400">{stats.goals_against_count}</div>
                  <div className="text-xs font-body text-gray-400">Encaissés</div>
                </div>
              </div>
              <div className="mt-4 text-xs font-accent text-gray-300">
                Diff: {stats.goals_diff > 0 ? '+' : ''}{stats.goals_diff}
              </div>
            </motion.div>

            {/* Forme */}
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
            >
              <div className="flex items-center justify-between mb-4">
                <Activity className="text-[#BDFF00]" size={28} />
                <span className="text-xs font-accent text-gray-400 uppercase tracking-wider">Forme</span>
              </div>
              <div className={`text-3xl font-title font-bold mb-2 ${getPerformanceColor(formePercentage)}`}>
                {formePercentage}%
              </div>
              <div className="flex gap-1 mb-2">
                {(stats.forme || []).slice(-5).map((result, index) => (
                  <span
                    key={`forme-result-${index}-${result}`}
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      result === 'V' ? 'bg-green-500' :
                      result === 'N' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                  >
                    {result}
                  </span>
                ))}
              </div>
              <div className="text-xs font-accent text-gray-300">
                5 derniers matchs
              </div>
            </motion.div>
          </motion.div>
        )}

        {activeTab === 'performance' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {/* Taux de victoire */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-8 text-center"
            >
              <TrendingUp className="text-[#FFE400] mx-auto mb-4" size={36} />
              <div className={`text-4xl font-title font-bold mb-2 ${getPerformanceColor(winRate)}`}>
                {winRate}%
              </div>
              <div className="font-accent text-gray-300 mb-4">Taux de victoire</div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${winRate}%` }}
                ></div>
              </div>
            </motion.div>

            {/* Moyenne buts marqués */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-8 text-center"
            >
              <Target className="text-[#BDFF00] mx-auto mb-4" size={36} />
              <div className="text-4xl font-title font-bold text-green-400 mb-2">
                {avgGoalsFor}
              </div>
              <div className="font-accent text-gray-300 mb-4">Buts/match (pour)</div>
              <div className="text-sm text-gray-400">
                Total: {stats.goals_for_count} buts
              </div>
            </motion.div>

            {/* Moyenne buts encaissés */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-8 text-center"
            >
              <Users className="text-[#FFE400] mx-auto mb-4" size={36} />
              <div className="text-4xl font-title font-bold text-red-400 mb-2">
                {avgGoalsAgainst}
              </div>
              <div className="font-accent text-gray-300 mb-4">Buts/match (contre)</div>
              <div className="text-sm text-gray-400">
                Total: {stats.goals_against_count} buts
              </div>
            </motion.div>
          </motion.div>
        )}

        {activeTab === 'attaque' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {/* Efficacité offensive */}
            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="font-title text-xl font-bold text-[#FFE400] mb-6">Efficacité Offensive</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-body text-gray-300">Buts marqués</span>
                  <span className="font-title text-xl font-bold text-green-400">{stats.goals_for_count}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-gray-300">Moyenne par match</span>
                  <span className="font-title text-xl font-bold text-[#BDFF00]">{avgGoalsFor}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-gray-300">Matchs avec but</span>
                  <span className="font-title text-xl font-bold text-[#FFE400]">
                    {Math.round((stats.goals_for_count > 0 ? totalMatchs : 0) / totalMatchs * 100)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Solidité défensive */}
            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="font-title text-xl font-bold text-[#BDFF00] mb-6">Solidité Défensive</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-body text-gray-300">Buts encaissés</span>
                  <span className="font-title text-xl font-bold text-red-400">{stats.goals_against_count}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-gray-300">Moyenne par match</span>
                  <span className="font-title text-xl font-bold text-orange-400">{avgGoalsAgainst}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-gray-300">Clean sheets</span>
                  <span className="font-title text-xl font-bold text-[#FFE400]">
                    {totalMatchs - Math.ceil(stats.goals_against_count / Math.max(avgGoalsAgainst, 1))}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center mt-12"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] font-accent font-bold px-8 py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center gap-3">
              <Star size={20} />
              <span>Voir toutes les statistiques</span>
            </div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default StatsN2Section;